import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { apiService } from '../../services/api';
import BuildGraphMenu from './BuildGraphMenu';
import ExecutionInsightDetail from './ExecutionInsightDetail';
import SuggestedChangesPanel from './SuggestedChangesPanel';
import './BuildGraph.css';
import './ReviewFeedbackPage.css';

const STATUS_OPTIONS = [
  { value: 'all', label: 'All statuses' },
  { value: 'open', label: 'Open' },
  { value: 'addressed', label: 'Addressed' },
  { value: 'dismissed', label: 'Dismissed' },
];

const RATING_OPTIONS = [
  { value: 'all', label: 'All feedback' },
  { value: '1', label: 'Positive' },
  { value: '-1', label: 'Negative' },
];

function ReviewFeedbackPage() {
  const { graphId } = useParams();
  const navigate = useNavigate();

  const [feedback, setFeedback] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filterRating, setFilterRating] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchText, setSearchText] = useState('');

  const [selectedFeedbackId, setSelectedFeedbackId] = useState(null);
  const [insightDetail, setInsightDetail] = useState(null);
  const [insightLoading, setInsightLoading] = useState(false);
  const [insightError, setInsightError] = useState(null);

  const selectedFeedback = useMemo(
    () => feedback.find((f) => f.feedback_id === selectedFeedbackId) || null,
    [feedback, selectedFeedbackId],
  );

  const loadFeedback = useCallback(async () => {
    if (!graphId) return;
    setLoading(true);
    setError(null);
    try {
      const params = {
        page: 1,
        pageSize: 100,
      };
      if (filterRating !== 'all') params.rating = filterRating;
      if (filterStatus !== 'all') params.status = filterStatus;
      const res = await apiService.listGraphFeedback(graphId, params);
      if (res?.success) {
        setFeedback(res.feedback || []);
      } else {
        setError(res?.error || 'Failed to load feedback');
      }
    } catch (err) {
      console.error('Failed to load feedback:', err);
      setError(err?.response?.data?.error || err.message || 'Failed to load feedback');
    } finally {
      setLoading(false);
    }
  }, [graphId, filterRating, filterStatus]);

  useEffect(() => {
    loadFeedback();
  }, [loadFeedback]);

  const filteredFeedback = useMemo(() => {
    if (!searchText.trim()) return feedback;
    const needle = searchText.trim().toLowerCase();
    return feedback.filter((f) => {
      const haystack = [
        f.comment,
        f.insight_query,
        f.insight_answer,
        f.insight_intent,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(needle);
    });
  }, [feedback, searchText]);

  const handleSelectRow = useCallback(async (row) => {
    setSelectedFeedbackId(row.feedback_id);
    setInsightDetail(null);
    setInsightError(null);
    if (!row.insight_id) return;
    setInsightLoading(true);
    try {
      const res = await apiService.getExecutionInsight(row.insight_id);
      if (res?.success && res.insight) {
        setInsightDetail(res.insight);
      } else {
        setInsightError(res?.error || 'Insight not found');
      }
    } catch (err) {
      console.error('Failed to load insight:', err);
      setInsightError(err?.response?.data?.error || err.message || 'Failed to load insight');
    } finally {
      setInsightLoading(false);
    }
  }, []);

  const handleStatusChange = useCallback(async (row, newStatus) => {
    try {
      const res = await apiService.updateInsightFeedback(row.feedback_id, { status: newStatus });
      if (res?.success && res.feedback) {
        setFeedback((prev) =>
          prev.map((f) => (f.feedback_id === row.feedback_id ? { ...f, ...res.feedback } : f)),
        );
      }
    } catch (err) {
      console.error('Failed to update feedback status:', err);
    }
  }, []);

  const handleSuggestionsUpdated = useCallback((updated) => {
    if (!updated) return;
    setFeedback((prev) =>
      prev.map((f) => (f.feedback_id === updated.feedback_id ? { ...f, ...updated } : f)),
    );
  }, []);

  const openFullView = useCallback(() => {
    if (!selectedFeedback?.insight_id) return;
    navigate(
      `/user-home/graphs/build/${graphId}/build-tune/explainability?insight=${selectedFeedback.insight_id}`,
    );
  }, [graphId, navigate, selectedFeedback]);

  const closeDrawer = () => {
    setSelectedFeedbackId(null);
    setInsightDetail(null);
    setInsightError(null);
  };

  return (
    <div className="build-graph-page">
      <BuildGraphMenu graphId={graphId} />

      <div className="review-feedback-header">
        <div>
          <h2>Review Feedback</h2>
          <p className="review-feedback-subtitle">
            Browse user feedback on chat answers, jump to the underlying execution insight, and
            see AI-suggested graph changes for each one.
          </p>
        </div>
        <button type="button" className="btn-secondary btn-sm" onClick={loadFeedback} disabled={loading}>
          {loading ? 'Refreshing…' : '↻ Refresh'}
        </button>
      </div>

      <div className="review-feedback-controls">
        <div className="review-feedback-control">
          <label>Rating</label>
          <select value={filterRating} onChange={(e) => setFilterRating(e.target.value)}>
            {RATING_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        <div className="review-feedback-control">
          <label>Status</label>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        <div className="review-feedback-control review-feedback-control--grow">
          <label>Search</label>
          <input
            type="text"
            placeholder="Search comments and questions"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </div>
      </div>

      {error && <div className="review-feedback-error">{error}</div>}

      <div className={`review-feedback-layout ${selectedFeedbackId ? 'has-drawer' : ''}`}>
        <div className="review-feedback-table-wrap">
          {loading && feedback.length === 0 ? (
            <div className="review-feedback-empty">Loading feedback…</div>
          ) : filteredFeedback.length === 0 ? (
            <div className="review-feedback-empty">
              {feedback.length === 0
                ? 'No feedback recorded yet for this graph. Submit feedback from the Test Graph chat to see it here.'
                : 'No feedback matches your filters.'}
            </div>
          ) : (
            <table className="review-feedback-table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Rating</th>
                  <th>Question / Answer</th>
                  <th>Comment</th>
                  <th>Score</th>
                  <th>Status</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {filteredFeedback.map((row) => {
                  const isSelected = row.feedback_id === selectedFeedbackId;
                  const ratingLabel = row.rating === 1
                    ? 'Positive'
                    : row.rating === -1 ? 'Negative' : 'Neutral';
                  const ratingClass = row.rating === 1
                    ? 'rating-pill rating-positive'
                    : row.rating === -1 ? 'rating-pill rating-negative' : 'rating-pill';
                  return (
                    <tr
                      key={row.feedback_id}
                      className={isSelected ? 'is-selected' : ''}
                      onClick={() => handleSelectRow(row)}
                    >
                      <td className="cell-when">{row.created_at ? new Date(row.created_at).toLocaleString() : '-'}</td>
                      <td><span className={ratingClass}>{ratingLabel}</span></td>
                      <td className="cell-qa">
                        <div className="cell-question" title={row.insight_query || ''}>
                          {truncate(row.insight_query, 90)}
                        </div>
                        {row.insight_answer && (
                          <div className="cell-answer" title={row.insight_answer}>
                            {truncate(row.insight_answer, 110)}
                          </div>
                        )}
                      </td>
                      <td className="cell-comment" title={row.comment || ''}>
                        {row.comment ? truncate(row.comment, 140) : <span className="muted">—</span>}
                      </td>
                      <td className="cell-score">
                        {typeof row.insight_score === 'number'
                          ? `${(row.insight_score * 100).toFixed(0)}%`
                          : '—'}
                      </td>
                      <td className="cell-status" onClick={(e) => e.stopPropagation()}>
                        <select
                          value={row.status || 'open'}
                          onChange={(e) => handleStatusChange(row, e.target.value)}
                        >
                          <option value="open">Open</option>
                          <option value="addressed">Addressed</option>
                          <option value="dismissed">Dismissed</option>
                        </select>
                      </td>
                      <td className="cell-actions" onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          className="btn-secondary btn-xs"
                          onClick={() => handleSelectRow(row)}
                        >
                          {isSelected ? 'Showing' : 'Open'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {selectedFeedbackId && (
          <aside className="review-feedback-drawer">
            <div className="drawer-header">
              <div>
                <div className="drawer-eyebrow">Feedback</div>
                <h3 className="drawer-title">
                  {truncate(selectedFeedback?.insight_query, 80) || 'Execution insight'}
                </h3>
              </div>
              <div className="drawer-header-actions">
                <button
                  type="button"
                  className="btn-secondary btn-sm"
                  onClick={openFullView}
                  disabled={!selectedFeedback?.insight_id}
                >
                  Open Full View
                </button>
                <button
                  type="button"
                  className="btn-secondary btn-sm drawer-close"
                  onClick={closeDrawer}
                  aria-label="Close"
                >
                  ×
                </button>
              </div>
            </div>

            {selectedFeedback && (
              <SuggestedChangesPanel
                feedback={selectedFeedback}
                graphId={graphId}
                onUpdated={handleSuggestionsUpdated}
              />
            )}

            <div className="drawer-section">
              <div className="drawer-section-title">Execution Insight</div>
              {insightLoading ? (
                <div className="drawer-loading">Loading insight…</div>
              ) : insightError ? (
                <div className="drawer-error">{insightError}</div>
              ) : insightDetail ? (
                <ExecutionInsightDetail insight={insightDetail} graphId={graphId} />
              ) : (
                <div className="drawer-empty">Select a feedback row to load its insight.</div>
              )}
            </div>
          </aside>
        )}
      </div>
    </div>
  );
}

function truncate(str, n) {
  if (!str) return '';
  return str.length > n ? `${str.substring(0, n - 1)}…` : str;
}

export default ReviewFeedbackPage;
